import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { generateTokenAuthHeader } from "../../commonfunctions/authentication";
import { log } from "../../commonfunctions/logger";
import { transformBrandToUserReadable } from "../../commonfunctions/text-transform";
import ProductListLoading, { ProductListSkeletonGrid } from "./Skeleton";
import { Container } from "../../components/styled/styled-components";
import Toast from "../../components/Toast/Toast";
import { useDocumentTitle } from "../../hooks/document-title";
import { AlertType } from "../../interfaces/enums";
import { AlertContent, ProductListAPIResponse } from "../../interfaces/interfaces";
import ProductCard from "./ProductCard/ProductCard";
import { Context, IContext } from "../../context/ContextApp";
import ProductFilter from "./productFilters";

const PER_PAGE = 20;

const ProductList = () => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [productResponse, setProductResponse] = useState<ProductListAPIResponse | null>(null);
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
  const [toast, setToast] = useState<AlertContent | null>(null);
  const location = useLocation();
  const params = useParams<{ brand: string; slug: string }>();
  const { appBranch }: IContext = useContext(Context);
  const [selectedTag, setSelectedTag] = useState<string | null>(null);
  const isEmptyProducts = Boolean(productResponse?.products?.length === 0);
  const loaderRef = useRef<HTMLDivElement | null>(null);
  // Set the document title
  const documentTitle = params.brand
    ? `Browse ${transformBrandToUserReadable(params.brand)} products`
    : "Browse products";
  useDocumentTitle(documentTitle);

  const fetchProducts = useCallback(
    async (page: number, append = false) => {
      if (append) {
        setIsLoadingMore(true);
      } else {
        setIsLoading(true);
      }

      try {
        const baseUrl = `${process.env.REACT_APP_HONEYBEE_ROOT_API}/products/${appBranch}/`;
        let url = `${baseUrl}${location.search}&caller=webstore&per_page=${PER_PAGE}&page=${page}`;
        if (params.brand) {
          url += `&brand=${params.brand}`;
        }
        const res = await fetch(url, generateTokenAuthHeader());
        if (res.ok) {
          const data = await res.json();
          if (data) {
            setProductResponse((previous) => {
              if (append && previous) {
                return {
                  ...data,
                  products: [...previous.products, ...data.products],
                };
              }
              return data;
            });
          }
        } else {
          const error = await res.text();
          throw error;
        }
      } catch (error) {
        log(error, "Failed to load products");
        setToast({
          message: "Failed to load products.",
          type: AlertType.Error,
        });
      } finally {
        if (append) {
          setIsLoadingMore(false);
        } else {
          setIsLoading(false);
        }
      }
    },
    [appBranch, location.search, params.brand],
  );

  // Initial fetch of products
  useEffect(() => {
    setProductResponse(null);
    fetchProducts(1, false);
  }, [location.search, params.brand, appBranch, fetchProducts]);

  // Set the selected tag from the query params
  useEffect(() => {
    const query = new URLSearchParams(location.search);
    const tag = query.get("tag");
    const search = query.get("search");
    if (search) {
      setSelectedTag("new");
    } else {
      setSelectedTag(tag || "all");
    }
  }, [location.search]);

  // Load more products when the user scrolls to the bottom of the page
  useEffect(() => {
    if (!loaderRef.current) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          if (!productResponse?.next || isLoadingMore || isLoading) return;

          fetchProducts(productResponse.next, true);
        });
      },
      { threshold: 0.1 },
    );
    observer.observe(loaderRef.current);

    return () => {
      observer.disconnect();
    };
  }, [fetchProducts, isLoading, isLoadingMore, productResponse?.next]);

  // Need to remove this variable and the below condition (if (lotusPolo.includes(product.slug)) return<></>) when the Lotus Polo is in stock
  const lotusPolo = ["p515ls", "p515ms"];

  // Loading case
  if (isLoading) {
    return (
      <div className="product-list-page">
        <ProductListLoading />
      </div>
    );
  }

  // Empty products case
  if (isEmptyProducts) {
    return (
      <div className="product-list-page">
        <ProductFilter params={params} selectedTag={selectedTag} />
        <div className="product-list-empty">
          <h2>No products found</h2>
          <p>Try a different tag or clear the filters to see more styles.</p>
        </div>
      </div>
    );
  }

  // Error fetch case
  if (!productResponse) {
    return (
      <div className="product-list-page">
        <Toast content={toast} onClick={() => setToast(null)} />
      </div>
    );
  }

  const hasMoreProducts = productResponse?.next !== null && productResponse?.next !== undefined;

  // Success Case
  return (
    <div className="product-list-page">
      <ProductFilter params={params} selectedTag={selectedTag} />
      <Container row>
        {productResponse.products.map((product, index) => {
          if (lotusPolo.includes(product.slug)) return <></>;
          if (!product.slug.includes("-promo")) {
            return <ProductCard product={product} key={index} displayBadge />;
          } else return <></>;
        })}
      </Container>
      {hasMoreProducts && (
        <div className="product-list-show-more" ref={loaderRef}>
          {isLoadingMore ? <ProductListSkeletonGrid /> : null}
        </div>
      )}
    </div>
  );
};

export default ProductList;
